import { effect as _$effect } from "@opentui/solid";
import { createTextNode as _$createTextNode } from "@opentui/solid";
import { insertNode as _$insertNode } from "@opentui/solid";
import { insert as _$insert } from "@opentui/solid";
import { memo as _$memo } from "@opentui/solid";
import { setProp as _$setProp } from "@opentui/solid";
import { use as _$use } from "@opentui/solid";
import { createElement as _$createElement } from "@opentui/solid";
import { createComponent as _$createComponent } from "@opentui/solid";
import { createEffect, createMemo, onMount, createSignal, onCleanup, on, Show, Switch, Match } from "solid-js";
import "opentui-spinner/solid";
import path from "path";
import { Filesystem } from "@/util/filesystem";
import { useLocal } from "@tui/context/local";
import { useTheme } from "@tui/context/theme";
import { EmptyBorder } from "@tui/component/border";
import { useSDK } from "@tui/context/sdk";
import { useRoute } from "@tui/context/route";
import { useSync } from "@tui/context/sync";
import { Identifier } from "@/id/id";
import { createStore, produce } from "solid-js/store";
import { useKeybind } from "@tui/context/keybind";
import { usePromptHistory } from "./history";
import { usePromptStash } from "./stash";
import { DialogStash } from "../dialog-stash";
import { Autocomplete } from "./autocomplete";
import { useCommandDialog } from "../dialog-command";
import { useRenderer } from "@opentui/solid";
import { Editor } from "@tui/util/editor";
import { useExit } from "../../context/exit";
import { Clipboard } from "../../util/clipboard";
import { TuiEvent } from "../../event";
import { iife } from "@/util/iife";
import { Locale } from "@/util/locale";
import { formatDuration } from "@/util/format";
import { createColors, createFrames } from "../../ui/spinner.ts";
import { useDialog } from "@tui/ui/dialog";
import { DialogProvider as DialogProviderConnect } from "../dialog-provider";
import { DialogAlert } from "../../ui/dialog-alert";
import { useToast } from "../../ui/toast";
import { useKV } from "../../context/kv";
import { useTextareaKeybindings } from "../textarea-keybindings";
import { DialogSkill } from "../dialog-skill";
const PLACEHOLDERS = ["Fix a TODO in the codebase", "What is the tech stack of this project?", "Fix broken tests"];
const SHELL_PLACEHOLDERS = ["ls -la", "git status", "pwd"];
export function Prompt(props) {
  let input;
  let anchor;
  let autocomplete;
  const keybind = useKeybind();
  const local = useLocal();
  const sdk = useSDK();
  const route = useRoute();
  const sync = useSync();
  const dialog = useDialog();
  const toast = useToast();
  const status = createMemo(() => sync.data.session_status?.[props.sessionID ?? ""] ?? {
    type: "idle"
  });
  const history = usePromptHistory();
  const stash = usePromptStash();
  const command = useCommandDialog();
  const renderer = useRenderer();
  const {
    theme,
    syntax
  } = useTheme();
  const kv = useKV();
  function promptModelWarning() {
    toast.show({
      variant: "warning",
      message: "Connect a provider to send prompts",
      duration: 3000
    });
    if (sync.data.provider.length === 0) {
      dialog.replace(() => _$createComponent(DialogProviderConnect, {}));
    }
  }
  const textareaKeybindings = useTextareaKeybindings();
  const fileStyleId = syntax().getStyleId("extmark.file");
  const agentStyleId = syntax().getStyleId("extmark.agent");
  const pasteStyleId = syntax().getStyleId("extmark.paste");
  let promptPartTypeId = 0;
  sdk.event.on(TuiEvent.PromptAppend.type, evt => {
    if (!input || input.isDestroyed) return;
    input.insertText(evt.properties.text);
    setTimeout(() => {
      // setTimeout is a workaround and needs to be addressed properly
      if (!input || input.isDestroyed) return;
      input.getLayoutNode().markDirty();
      input.gotoBufferEnd();
      renderer.requestRender();
    }, 0);
  });
  createEffect(() => {
    if (props.disabled) input.cursorColor = theme.backgroundElement;
    if (!props.disabled) input.cursorColor = theme.text;
  });
  const lastUserMessage = createMemo(() => {
    if (!props.sessionID) return undefined;
    const messages = sync.data.message[props.sessionID];
    if (!messages) return undefined;
    return messages.findLast(m => m.role === "user");
  });
  const [store, setStore] = createStore({
    placeholder: Math.floor(Math.random() * PLACEHOLDERS.length),
    prompt: {
      input: "",
      parts: []
    },
    mode: "normal",
    extmarkToPartIndex: new Map(),
    interrupt: 0
  });
  createEffect(on(() => props.sessionID, () => {
    setStore("placeholder", Math.floor(Math.random() * PLACEHOLDERS.length));
  }, {
    defer: true
  }));

  // Initialize agent/model/variant from last user message when session changes
  let syncedSessionID;
  createEffect(() => {
    const sessionID = props.sessionID;
    const msg = lastUserMessage();
    if (sessionID !== syncedSessionID) {
      if (!sessionID || !msg) return;
      syncedSessionID = sessionID;

      // Only set agent if it's a primary agent (not a subagent)
      const isPrimaryAgent = local.agent.list().some(x => x.name === msg.agent);
      if (msg.agent && isPrimaryAgent) {
        local.agent.set(msg.agent);
        if (msg.model) local.model.set(msg.model);
        if (msg.variant) local.model.variant.set(msg.variant);
      }
    }
  });
  command.register(() => {
    return [{
      title: "Clear prompt",
      value: "prompt.clear",
      category: "Prompt",
      hidden: true,
      onSelect: dialog => {
        input.extmarks.clear();
        input.clear();
        dialog.clear();
      }
    }, {
      title: "Submit prompt",
      value: "prompt.submit",
      keybind: "input_submit",
      category: "Prompt",
      hidden: true,
      onSelect: dialog => {
        if (!input.focused) return;
        submit();
        dialog.clear();
      }
    }, {
      title: "Paste",
      value: "prompt.paste",
      keybind: "input_paste",
      category: "Prompt",
      hidden: true,
      onSelect: async () => {
        const content = await Clipboard.read();
        if (content?.mime.startsWith("image/")) {
          await pasteImage({
            filename: "clipboard",
            mime: content.mime,
            content: content.data
          });
        }
      }
    }, {
      title: "Interrupt session",
      value: "session.interrupt",
      keybind: "session_interrupt",
      category: "Session",
      hidden: true,
      enabled: status().type !== "idle",
      onSelect: dialog => {
        if (autocomplete.visible) return;
        if (!input.focused) return;
        // TODO: this should be its own command
        if (store.mode === "shell") {
          setStore("mode", "normal");
          return;
        }
        if (!props.sessionID) return;
        setStore("interrupt", store.interrupt + 1);
        setTimeout(() => {
          setStore("interrupt", 0);
        }, 5000);
        if (store.interrupt >= 2) {
          sdk.client.session.abort({
            sessionID: props.sessionID
          });
          setStore("interrupt", 0);
        }
        dialog.clear();
      }
    }, {
      title: "Open editor",
      category: "Session",
      keybind: "editor_open",
      value: "prompt.editor",
      slash: {
        name: "editor"
      },
      onSelect: async dialog => {
        dialog.clear();

        // replace summarized text parts with the actual text
        const text = store.prompt.parts.filter(p => p.type === "text").reduce((acc, p) => {
          if (!p.source) return acc;
          return acc.replace(p.source.text.value, p.text);
        }, store.prompt.input);
        const nonTextParts = store.prompt.parts.filter(p => p.type !== "text");
        const value = text;
        const content = await Editor.open({
          value,
          renderer
        });
        if (!content) return;
        input.setText(content);

        // Update positions for nonTextParts based on their location in new content
        // Filter out parts whose virtual text was deleted
        // this handles a case where the user edits the text in the editor
        // such that the virtual text moves around or is deleted
        const updatedNonTextParts = nonTextParts.map(part => {
          let virtualText = "";
          if (part.type === "file" && part.source?.text) {
            virtualText = part.source.text.value;
          } else if (part.type === "agent" && part.source) {
            virtualText = part.source.value;
          }
          if (!virtualText) return part;
          const newStart = content.indexOf(virtualText);
          // if the virtual text is deleted, remove the part
          if (newStart === -1) return null;
          const newEnd = newStart + virtualText.length;
          if (part.type === "file" && part.source?.text) {
            return {
              ...part,
              source: {
                ...part.source,
                text: {
                  ...part.source.text,
                  start: newStart,
                  end: newEnd
                }
              }
            };
          }
          if (part.type === "agent" && part.source) {
            return {
              ...part,
              source: {
                ...part.source,
                start: newStart,
                end: newEnd
              }
            };
          }
          return part;
        }).filter(part => part !== null);
        setStore("prompt", {
          input: content,
          // keep only the non-text parts because the text parts were
          // already expanded inline
          parts: updatedNonTextParts
        });
        restoreExtmarksFromParts(updatedNonTextParts);
        input.cursorOffset = Bun.stringWidth(content);
      }
    }, {
      title: "Skills",
      value: "prompt.skills",
      category: "Prompt",
      slash: {
        name: "skills"
      },
      onSelect: () => {
        dialog.replace(() => _$createComponent(DialogSkill, {
          onSelect: skill => {
            input.setText(`/${skill} `);
            setStore("prompt", {
              input: `/${skill} `,
              parts: []
            });
            input.gotoBufferEnd();
          }
        }));
      }
    }];
  });
  const ref = {
    get focused() {
      return input.focused;
    },
    get current() {
      return store.prompt;
    },
    focus() {
      input.focus();
    },
    blur() {
      input.blur();
    },
    set(prompt) {
      input.setText(prompt.input);
      setStore("prompt", prompt);
      restoreExtmarksFromParts(prompt.parts);
      input.gotoBufferEnd();
    },
    reset() {
      input.clear();
      input.extmarks.clear();
      setStore("prompt", {
        input: "",
        parts: []
      });
      setStore("extmarkToPartIndex", new Map());
    },
    submit() {
      submit();
    }
  };
  createEffect(() => {
    if (props.visible !== false) input?.focus();
    if (props.visible === false) input?.blur();
  });
  function restoreExtmarksFromParts(parts) {
    input.extmarks.clear();
    setStore("extmarkToPartIndex", new Map());
    parts.forEach((part, partIndex) => {
      let start = 0;
      let end = 0;
      let virtualText = "";
      let styleId;
      if (part.type === "file" && part.source?.text) {
        start = part.source.text.start;
        end = part.source.text.end;
        virtualText = part.source.text.value;
        styleId = fileStyleId;
      } else if (part.type === "agent" && part.source) {
        start = part.source.start;
        end = part.source.end;
        virtualText = part.source.value;
        styleId = agentStyleId;
      } else if (part.type === "text" && part.source?.text) {
        start = part.source.text.start;
        end = part.source.text.end;
        virtualText = part.source.text.value;
        styleId = pasteStyleId;
      }
      if (virtualText) {
        const extmarkId = input.extmarks.create({
          start,
          end,
          virtual: true,
          styleId,
          typeId: promptPartTypeId
        });
        setStore("extmarkToPartIndex", map => {
          const newMap = new Map(map);
          newMap.set(extmarkId, partIndex);
          return newMap;
        });
      }
    });
  }
  function syncExtmarksWithPromptParts() {
    const allExtmarks = input.extmarks.getAllForTypeId(promptPartTypeId);
    setStore(produce(draft => {
      const newMap = new Map();
      const newParts = [];
      for (const extmark of allExtmarks) {
        const partIndex = draft.extmarkToPartIndex.get(extmark.id);
        if (partIndex !== undefined) {
          const part = draft.prompt.parts[partIndex];
          if (part) {
            if (part.type === "agent" && part.source) {
              part.source.start = extmark.start;
              part.source.end = extmark.end;
            } else if (part.type === "file" && part.source?.text) {
              part.source.text.start = extmark.start;
              part.source.text.end = extmark.end;
            } else if (part.type === "text" && part.source?.text) {
              part.source.text.start = extmark.start;
              part.source.text.end = extmark.end;
            }
            newMap.set(extmark.id, newParts.length);
            newParts.push(part);
          }
        }
      }
      draft.extmarkToPartIndex = newMap;
      draft.prompt.parts = newParts;
    }));
  }
  command.register(() => [{
    title: "Stash prompt",
    value: "prompt.stash",
    category: "Prompt",
    enabled: !!store.prompt.input,
    onSelect: dialog => {
      if (!store.prompt.input) return;
      stash.push({
        input: store.prompt.input,
        parts: store.prompt.parts
      });
      input.extmarks.clear();
      input.clear();
      setStore("prompt", {
        input: "",
        parts: []
      });
      setStore("extmarkToPartIndex", new Map());
      dialog.clear();
    }
  }, {
    title: "Stash pop",
    value: "prompt.stash.pop",
    category: "Prompt",
    enabled: stash.list().length > 0,
    onSelect: dialog => {
      const entry = stash.pop();
      if (entry) {
        input.setText(entry.input);
        setStore("prompt", {
          input: entry.input,
          parts: entry.parts
        });
        restoreExtmarksFromParts(entry.parts);
        input.gotoBufferEnd();
      }
      dialog.clear();
    }
  }, {
    title: "Stash list",
    value: "prompt.stash.list",
    category: "Prompt",
    enabled: stash.list().length > 0,
    onSelect: dialog => {
      dialog.replace(() => _$createComponent(DialogStash, {
        onSelect: entry => {
          input.setText(entry.input);
          setStore("prompt", {
            input: entry.input,
            parts: entry.parts
          });
          restoreExtmarksFromParts(entry.parts);
          input.gotoBufferEnd();
        }
      }));
    }
  }]);
  async function submit() {
    if (props.disabled) return;
    if (autocomplete?.visible) return;
    if (!store.prompt.input) return;
    const trimmed = store.prompt.input.trim();
    if (trimmed === "exit" || trimmed === "quit" || trimmed === ":q") {
      exit();
      return;
    }
    const selectedModel = local.model.current();
    if (!selectedModel) {
      promptModelWarning();
      return;
    }
    const sessionID = props.sessionID ? props.sessionID : await (async () => {
      const sessionID = await sdk.client.session.create({}).then(x => x.data.id);
      return sessionID;
    })();
    const messageID = Identifier.ascending("message");
    let inputText = store.prompt.input;

    // Expand pasted text inline before submitting
    const allExtmarks = input.extmarks.getAllForTypeId(promptPartTypeId);
    const sortedExtmarks = allExtmarks.sort((a, b) => b.start - a.start);
    for (const extmark of sortedExtmarks) {
      const partIndex = store.extmarkToPartIndex.get(extmark.id);
      if (partIndex !== undefined) {
        const part = store.prompt.parts[partIndex];
        if (part?.type === "text" && part.text) {
          const before = inputText.slice(0, extmark.start);
          const after = inputText.slice(extmark.end);
          inputText = before + part.text + after;
        }
      }
    }

    // Filter out text parts (pasted content) since they're now expanded inline
    const nonTextParts = store.prompt.parts.filter(part => part.type !== "text");

    // Capture mode before it gets reset
    const currentMode = store.mode;
    const variant = local.model.variant.current();
    if (store.mode === "shell") {
      sdk.client.session.shell({
        sessionID,
        agent: local.agent.current().name,
        model: {
          providerID: selectedModel.providerID,
          modelID: selectedModel.modelID
        },
        command: inputText
      });
      setStore("mode", "normal");
    } else if (inputText.startsWith("/") && iife(() => {
      const firstLine = inputText.split("\n")[0];
      const command = firstLine.split(" ")[0].slice(1);
      return sync.data.command.some(x => x.name === command);
    })) {
      // Parse command from first line, preserve multi-line content in arguments
      const firstLineEnd = inputText.indexOf("\n");
      const firstLine = firstLineEnd === -1 ? inputText : inputText.slice(0, firstLineEnd);
      const [command, ...firstLineArgs] = firstLine.split(" ");
      const restOfInput = firstLineEnd === -1 ? "" : inputText.slice(firstLineEnd + 1);
      const args = firstLineArgs.join(" ") + (restOfInput ? "\n" + restOfInput : "");
      sdk.client.session.command({
        sessionID,
        command: command.slice(1),
        arguments: args,
        agent: local.agent.current().name,
        model: `${selectedModel.providerID}/${selectedModel.modelID}`,
        messageID,
        variant,
        parts: nonTextParts.filter(x => x.type === "file").map(x => ({
          id: Identifier.ascending("part"),
          ...x
        }))
      });
    } else {
      sdk.client.session.prompt({
        sessionID,
        ...selectedModel,
        messageID,
        agent: local.agent.current().name,
        model: selectedModel,
        variant,
        parts: [{
          id: Identifier.ascending("part"),
          type: "text",
          text: inputText
        }, ...nonTextParts.map(x => ({
          id: Identifier.ascending("part"),
          ...x
        }))]
      }).catch(() => {});
    }
    history.append({
      ...store.prompt,
      mode: currentMode
    });
    input.extmarks.clear();
    setStore("prompt", {
      input: "",
      parts: []
    });
    setStore("extmarkToPartIndex", new Map());
    props.onSubmit?.();

    // temporary hack to make sure the message is sent
    if (!props.sessionID) setTimeout(() => {
      route.navigate({
        type: "session",
        sessionID
      });
    }, 50);
    input.clear();
  }
  const exit = useExit();
  function pasteText(text, virtualText) {
    const currentOffset = input.visualCursor.offset;
    const extmarkStart = currentOffset;
    const extmarkEnd = extmarkStart + virtualText.length;
    input.insertText(virtualText + " ");
    const extmarkId = input.extmarks.create({
      start: extmarkStart,
      end: extmarkEnd,
      virtual: true,
      styleId: pasteStyleId,
      typeId: promptPartTypeId
    });
    setStore(produce(draft => {
      const partIndex = draft.prompt.parts.length;
      draft.prompt.parts.push({
        type: "text",
        text,
        source: {
          text: {
            start: extmarkStart,
            end: extmarkEnd,
            value: virtualText
          }
        }
      });
      draft.extmarkToPartIndex.set(extmarkId, partIndex);
    }));
  }
  async function pasteImage(file) {
    const currentOffset = input.visualCursor.offset;
    const extmarkStart = currentOffset;
    const count = store.prompt.parts.filter(x => x.type === "file" && x.mime.startsWith("image/")).length;
    const virtualText = `[Image ${count + 1}]`;
    const extmarkEnd = extmarkStart + virtualText.length;
    const textToInsert = virtualText + " ";
    input.insertText(textToInsert);
    const extmarkId = input.extmarks.create({
      start: extmarkStart,
      end: extmarkEnd,
      virtual: true,
      styleId: pasteStyleId,
      typeId: promptPartTypeId
    });
    const part = {
      type: "file",
      mime: file.mime,
      filename: file.filename,
      url: `data:${file.mime};base64,${file.content}`,
      source: {
        type: "file",
        path: file.filename ?? "",
        text: {
          start: extmarkStart,
          end: extmarkEnd,
          value: virtualText
        }
      }
    };
    setStore(produce(draft => {
      const partIndex = draft.prompt.parts.length;
      draft.prompt.parts.push(part);
      draft.extmarkToPartIndex.set(extmarkId, partIndex);
    }));
    return;
  }
  const highlight = createMemo(() => {
    if (keybind.leader) return theme.border;
    if (store.mode === "shell") return theme.primary;
    return local.agent.color(local.agent.current().name);
  });
  const showVariant = createMemo(() => {
    const variants = local.model.variant.list();
    if (variants.length === 0) return false;
    const current = local.model.variant.current();
    return !!current;
  });
  const placeholderText = createMemo(() => {
    if (props.sessionID) return undefined;
    if (store.mode === "shell") {
      const example = SHELL_PLACEHOLDERS[store.placeholder % SHELL_PLACEHOLDERS.length];
      return `Run a command... "${example}"`;
    }
    return `Ask anything... "${PLACEHOLDERS[store.placeholder % PLACEHOLDERS.length]}"`;
  });
  const spinnerDef = createMemo(() => {
    const color = local.agent.color(local.agent.current().name);
    return {
      frames: createFrames({
        color,
        style: "blocks",
        inactiveFactor: 0.6,
        // enableFading: false,
        minAlpha: 0.3
      }),
      color: createColors({
        color,
        style: "blocks",
        inactiveFactor: 0.6,
        // enableFading: false,
        minAlpha: 0.3
      })
    };
  });
  return [_$createComponent(Autocomplete, {
    get sessionID() {
      return props.sessionID;
    },
    ref: r => autocomplete = r,
    anchor: () => anchor,
    input: () => input,
    setPrompt: cb => {
      setStore("prompt", produce(cb));
    },
    setExtmark: (partIndex, extmarkId) => {
      setStore("extmarkToPartIndex", map => {
        const newMap = new Map(map);
        newMap.set(extmarkId, partIndex);
        return newMap;
      });
    },
    get value() {
      return store.prompt.input;
    },
    fileStyleId: fileStyleId,
    agentStyleId: agentStyleId,
    promptPartTypeId: () => promptPartTypeId
  }), (() => {
    var _el$ = _$createElement("box"),
      _el$2 = _$createElement("box"),
      _el$3 = _$createElement("box"),
      _el$4 = _$createElement("textarea"),
      _el$5 = _$createElement("box"),
      _el$6 = _$createElement("text"),
      _el$7 = _$createTextNode(` `),
      _el$13 = _$createElement("box"),
      _el$14 = _$createElement("box"),
      _el$15 = _$createElement("box");
    _$insertNode(_el$, _el$2);
    _$insertNode(_el$, _el$13);
    _$insertNode(_el$, _el$15);
    _$use(r => anchor = r, _el$);
    _$insertNode(_el$2, _el$3);
    _$setProp(_el$2, "border", ["left"]);
    _$insertNode(_el$3, _el$4);
    _$insertNode(_el$3, _el$5);
    _$setProp(_el$3, "paddingLeft", 2);
    _$setProp(_el$3, "paddingRight", 2);
    _$setProp(_el$3, "paddingTop", 1);
    _$setProp(_el$3, "flexShrink", 0);
    _$setProp(_el$3, "flexGrow", 1);
    _$use(r => {
      input = r;
      if (promptPartTypeId === 0) {
        promptPartTypeId = input.extmarks.registerType("prompt-part");
      }
      props.ref?.(ref);
      setTimeout(() => {
        // setTimeout is a workaround and needs to be addressed properly
        if (!input || input.isDestroyed) return;
        input.cursorColor = theme.text;
      }, 0);
    }, _el$4);
    _$setProp(_el$4, "minHeight", 1);
    _$setProp(_el$4, "maxHeight", 6);
    _$setProp(_el$4, "onContentChange", () => {
      const value = input.plainText;
      setStore("prompt", "input", value);
      autocomplete.onInput(value);
      syncExtmarksWithPromptParts();
    });
    _$setProp(_el$4, "onKeyDown", async e => {
      if (props.disabled) {
        e.preventDefault();
        return;
      }
      // Handle clipboard paste (Ctrl+V) - check for images first on Windows
      // This is needed because Windows terminal doesn't properly send image data
      // through bracketed paste, so we need to intercept the keypress and
      // directly read from clipboard before the terminal handles it
      if (keybind.match("input_paste", e)) {
        const content = await Clipboard.read();
        if (content?.mime.startsWith("image/")) {
          e.preventDefault();
          await pasteImage({
            filename: "clipboard",
            mime: content.mime,
            content: content.data
          });
          return;
        }
        // If no image, let the default paste behavior continue
      }
      if (keybind.match("input_clear", e) && store.prompt.input !== "") {
        input.clear();
        input.extmarks.clear();
        setStore("prompt", {
          input: "",
          parts: []
        });
        setStore("extmarkToPartIndex", new Map());
        return;
      }
      if (keybind.match("app_exit", e)) {
        if (store.prompt.input === "") {
          await exit();
          // Don't preventDefault - let textarea potentially handle the event
          e.preventDefault();
          return;
        }
      }
      if (e.name === "!" && input.visualCursor.offset === 0) {
        setStore("placeholder", Math.floor(Math.random() * SHELL_PLACEHOLDERS.length));
        setStore("mode", "shell");
        e.preventDefault();
        return;
      }
      if (store.mode === "shell") {
        if (e.name === "backspace" && input.visualCursor.offset === 0 || e.name === "escape") {
          setStore("mode", "normal");
          e.preventDefault();
          return;
        }
      }
      if (store.mode === "normal") autocomplete.onKeyDown(e);
      if (!autocomplete.visible) {
        if (keybind.match("history_previous", e) && input.cursorOffset === 0 || keybind.match("history_next", e) && input.cursorOffset === input.plainText.length) {
          const direction = keybind.match("history_previous", e) ? -1 : 1;
          const item = history.move(direction, input.plainText);
          if (item) {
            input.setText(item.input);
            setStore("prompt", item);
            setStore("mode", item.mode ?? "normal");
            restoreExtmarksFromParts(item.parts);
            e.preventDefault();
            if (direction === -1) input.cursorOffset = 0;
            if (direction === 1) input.cursorOffset = input.plainText.length;
          }
          return;
        }
        if (keybind.match("history_previous", e) && input.visualCursor.visualRow === 0) input.cursorOffset = 0;
        if (keybind.match("history_next", e) && input.visualCursor.visualRow === input.height - 1) input.cursorOffset = input.plainText.length;
      }
    });
    _$setProp(_el$4, "onSubmit", submit);
    _$setProp(_el$4, "onPaste", async event => {
      if (props.disabled) {
        event.preventDefault();
        return;
      }

      // Normalize line endings at the boundary
      // Windows ConPTY/Terminal often sends CR-only newlines in bracketed paste
      // Replace CRLF first, then any remaining CR
      const normalizedText = event.text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      const pastedContent = normalizedText.trim();
      if (!pastedContent) {
        command.trigger("prompt.paste");
        return;
      }

      // trim ' from the beginning and end of the pasted content. just
      // ' and nothing else
      const filepath = pastedContent.replace(/^'+|'+$/g, "").replace(/\\ /g, " ");
      const isUrl = /^(https?):\/\//.test(filepath);
      if (!isUrl) {
        try {
          const mime = Filesystem.mimeType(filepath);
          const filename = path.basename(filepath);
          // Handle SVG as raw text content, not as base64 image
          if (mime === "image/svg+xml") {
            event.preventDefault();
            const content = await Filesystem.readText(filepath).catch(() => {});
            if (content) {
              pasteText(content, `[SVG: ${filename ?? "image"}]`);
              return;
            }
          }
          if (mime.startsWith("image/")) {
            event.preventDefault();
            const content = await Filesystem.readArrayBuffer(filepath).then(buffer => Buffer.from(buffer).toString("base64")).catch(() => {});
            if (content) {
              await pasteImage({
                filename,
                mime,
                content
              });
              return;
            }
          }
        } catch {}
      }
      const lineCount = (pastedContent.match(/\n/g)?.length ?? 0) + 1;
      if ((lineCount >= 3 || pastedContent.length > 150) && !sync.data.config.experimental?.disable_paste_summary) {
        event.preventDefault();
        pasteText(pastedContent, `[Pasted ~${lineCount} lines]`);
        return;
      }

      // Force layout update and render for the pasted content
      setTimeout(() => {
        // setTimeout is a workaround and needs to be addressed properly
        if (!input || input.isDestroyed) return;
        input.getLayoutNode().markDirty();
        renderer.requestRender();
      }, 0);
    });
    _$setProp(_el$4, "onMouseDown", r => r.target?.focus());
    _$insertNode(_el$5, _el$6);
    _$setProp(_el$5, "flexDirection", "row");
    _$setProp(_el$5, "flexShrink", 0);
    _$setProp(_el$5, "paddingTop", 1);
    _$setProp(_el$5, "gap", 1);
    _$insertNode(_el$6, _el$7);
    _$insert(_el$6, (() => {
      var _c$ = _$memo(() => store.mode === "shell");
      return () => _c$() ? "Shell" : Locale.titlecase(local.agent.current().name);
    })(), _el$7);
    _$insert(_el$5, _$createComponent(Show, {
      get when() {
        return store.mode === "normal";
      },
      get children() {
        var _el$8 = _$createElement("box"),
          _el$9 = _$createElement("text"),
          _el$0 = _$createElement("text");
        _$insertNode(_el$8, _el$9);
        _$insertNode(_el$8, _el$0);
        _$setProp(_el$8, "flexDirection", "row");
        _$setProp(_el$8, "gap", 1);
        _$setProp(_el$9, "flexShrink", 0);
        _$insert(_el$9, () => local.model.parsed().model);
        _$insert(_el$0, () => local.model.parsed().provider);
        _$insert(_el$8, _$createComponent(Show, {
          get when() {
            return showVariant();
          },
          get children() {
            return [(() => {
              var _el$1 = _$createElement("text");
              _$insertNode(_el$1, _$createTextNode(`·`));
              _$effect(_$p => _$setProp(_el$1, "fg", theme.textMuted, _$p));
              return _el$1;
            })(), (() => {
              var _el$11 = _$createElement("text"),
                _el$12 = _$createElement("span");
              _$insertNode(_el$11, _el$12);
              _$insert(_el$12, () => local.model.variant.current());
              _$effect(_$p => _$setProp(_el$12, "style", {
                fg: theme.warning,
                bold: true
              }, _$p));
              return _el$11;
            })()];
          }
        }), null);
        _$effect(_p$ => {
          var _v$ = keybind.leader ? theme.textMuted : theme.text,
            _v$2 = theme.textMuted;
          _v$ !== _p$.e && (_p$.e = _$setProp(_el$9, "fg", _v$, _p$.e));
          _v$2 !== _p$.t && (_p$.t = _$setProp(_el$0, "fg", _v$2, _p$.t));
          return _p$;
        }, {
          e: undefined,
          t: undefined
        });
        return _el$8;
      }
    }), null);
    _$insertNode(_el$13, _el$14);
    _$setProp(_el$13, "height", 1);
    _$setProp(_el$13, "border", ["left"]);
    _$setProp(_el$14, "height", 1);
    _$setProp(_el$14, "border", ["bottom"]);
    _$setProp(_el$15, "flexDirection", "row");
    _$setProp(_el$15, "justifyContent", "space-between");
    _$insert(_el$15, _$createComponent(Show, {
      get when() {
        return status().type !== "idle";
      },
      get fallback() {
        return _$createElement("text");
      },
      get children() {
        var _el$16 = _$createElement("box"),
          _el$17 = _$createElement("box"),
          _el$18 = _$createElement("box"),
          _el$20 = _$createElement("box"),
          _el$21 = _$createElement("text"),
          _el$22 = _$createTextNode(`esc `),
          _el$24 = _$createElement("span");
        _$insertNode(_el$16, _el$17);
        _$insertNode(_el$16, _el$21);
        _$setProp(_el$16, "flexDirection", "row");
        _$setProp(_el$16, "gap", 1);
        _$setProp(_el$16, "flexGrow", 1);
        _$insertNode(_el$17, _el$18);
        _$insertNode(_el$17, _el$20);
        _$setProp(_el$17, "flexShrink", 0);
        _$setProp(_el$17, "flexDirection", "row");
        _$setProp(_el$17, "gap", 1);
        _$setProp(_el$18, "marginLeft", 1);
        _$insert(_el$18, _$createComponent(Show, {
          get when() {
            return kv.get("animations_enabled", true);
          },
          get fallback() {
            return (() => {
              var _el$43 = _$createElement("text");
              _$insertNode(_el$43, _$createTextNode(`[⋯]`));
              _$effect(_$p => _$setProp(_el$43, "fg", theme.textMuted, _$p));
              return _el$43;
            })();
          },
          get children() {
            var _el$19 = _$createElement("spinner");
            _$setProp(_el$19, "interval", 40);
            _$effect(_p$ => {
              var _v$3 = spinnerDef().color,
                _v$4 = spinnerDef().frames;
              _v$3 !== _p$.e && (_p$.e = _$setProp(_el$19, "color", _v$3, _p$.e));
              _v$4 !== _p$.t && (_p$.t = _$setProp(_el$19, "frames", _v$4, _p$.t));
              return _p$;
            }, {
              e: undefined,
              t: undefined
            });
            return _el$19;
          }
        }));
        _$setProp(_el$20, "flexDirection", "row");
        _$setProp(_el$20, "gap", 1);
        _$setProp(_el$20, "flexShrink", 0);
        _$insert(_el$20, () => {
          const retry = createMemo(() => {
            const s = status();
            if (s.type !== "retry") return;
            return s;
          });
          const message = createMemo(() => {
            const r = retry();
            if (!r) return;
            if (r.message.includes("exceeded your current quota") && r.message.includes("gemini")) return "gemini is way too hot right now";
            if (r.message.length > 80) return r.message.slice(0, 80) + "...";
            return r.message;
          });
          const isTruncated = createMemo(() => {
            const r = retry();
            if (!r) return false;
            return r.message.length > 120;
          });
          const [seconds, setSeconds] = createSignal(0);
          onMount(() => {
            const timer = setInterval(() => {
              const next = retry()?.next;
              if (next) setSeconds(Math.round((next - Date.now()) / 1000));
            }, 1000);
            onCleanup(() => {
              clearInterval(timer);
            });
          });
          const handleMessageClick = () => {
            const r = retry();
            if (!r) return;
            if (isTruncated()) {
              DialogAlert.show(dialog, "Retry Error", r.message);
            }
          };
          const retryText = () => {
            const r = retry();
            if (!r) return "";
            const baseMessage = message();
            const truncatedHint = isTruncated() ? " (click to expand)" : "";
            const duration = formatDuration(seconds());
            const retryInfo = ` [retrying ${duration ? `in ${duration} ` : ""}attempt #${r.attempt}]`;
            return baseMessage + truncatedHint + retryInfo;
          };
          return _$createComponent(Show, {
            get when() {
              return retry();
            },
            get children() {
              var _el$45 = _$createElement("box"),
                _el$46 = _$createElement("text");
              _$insertNode(_el$45, _el$46);
              _$setProp(_el$45, "onMouseUp", handleMessageClick);
              _$insert(_el$46, retryText);
              _$effect(_$p => _$setProp(_el$46, "fg", theme.error, _$p));
              return _el$45;
            }
          });
        });
        _$insertNode(_el$21, _el$22);
        _$insertNode(_el$21, _el$24);
        _$insert(_el$24, () => store.interrupt > 0 ? "again to interrupt" : "interrupt");
        _$effect(_p$ => {
          var _v$5 = status().type === "retry" ? "space-between" : "flex-start",
            _v$6 = store.interrupt > 0 ? theme.primary : theme.text,
            _v$7 = {
              fg: store.interrupt > 0 ? theme.primary : theme.textMuted
            };
          _v$5 !== _p$.e && (_p$.e = _$setProp(_el$16, "justifyContent", _v$5, _p$.e));
          _v$6 !== _p$.t && (_p$.t = _$setProp(_el$21, "fg", _v$6, _p$.t));
          _v$7 !== _p$.a && (_p$.a = _$setProp(_el$24, "style", _v$7, _p$.a));
          return _p$;
        }, {
          e: undefined,
          t: undefined,
          a: undefined
        });
        return _el$16;
      }
    }), null);
    _$insert(_el$15, _$createComponent(Show, {
      get when() {
        return status().type !== "retry";
      },
      get children() {
        var _el$25 = _$createElement("box");
        _$setProp(_el$25, "gap", 2);
        _$setProp(_el$25, "flexDirection", "row");
        _$insert(_el$25, _$createComponent(Switch, {
          get children() {
            return [_$createComponent(Match, {
              get when() {
                return store.mode === "normal";
              },
              get children() {
                return [_$createComponent(Show, {
                  get when() {
                    return local.model.variant.list().length > 0;
                  },
                  get children() {
                    var _el$26 = _$createElement("text"),
                      _el$27 = _$createTextNode(` `),
                      _el$28 = _$createElement("span");
                    _$insertNode(_el$26, _el$27);
                    _$insertNode(_el$26, _el$28);
                    _$insert(_el$26, () => keybind.print("variant_cycle"), _el$27);
                    _$insertNode(_el$28, _$createTextNode(`variants`));
                    _$effect(_p$ => {
                      var _v$8 = theme.text,
                        _v$9 = {
                          fg: theme.textMuted
                        };
                      _v$8 !== _p$.e && (_p$.e = _$setProp(_el$26, "fg", _v$8, _p$.e));
                      _v$9 !== _p$.t && (_p$.t = _$setProp(_el$28, "style", _v$9, _p$.t));
                      return _p$;
                    }, {
                      e: undefined,
                      t: undefined
                    });
                    return _el$26;
                  }
                }), (() => {
                  var _el$30 = _$createElement("text"),
                    _el$31 = _$createTextNode(` `),
                    _el$32 = _$createElement("span");
                  _$insertNode(_el$30, _el$31);
                  _$insertNode(_el$30, _el$32);
                  _$insert(_el$30, () => keybind.print("agent_cycle"), _el$31);
                  _$insertNode(_el$32, _$createTextNode(`agents`));
                  _$effect(_p$ => {
                    var _v$0 = theme.text,
                      _v$1 = {
                        fg: theme.textMuted
                      };
                    _v$0 !== _p$.e && (_p$.e = _$setProp(_el$30, "fg", _v$0, _p$.e));
                    _v$1 !== _p$.t && (_p$.t = _$setProp(_el$32, "style", _v$1, _p$.t));
                    return _p$;
                  }, {
                    e: undefined,
                    t: undefined
                  });
                  return _el$30;
                })(), (() => {
                  var _el$34 = _$createElement("text"),
                    _el$35 = _$createTextNode(` `),
                    _el$36 = _$createElement("span");
                  _$insertNode(_el$34, _el$35);
                  _$insertNode(_el$34, _el$36);
                  _$insert(_el$34, () => keybind.print("command_list"), _el$35);
                  _$insertNode(_el$36, _$createTextNode(`commands`));
                  _$effect(_p$ => {
                    var _v$10 = theme.text,
                      _v$11 = {
                        fg: theme.textMuted
                      };
                    _v$10 !== _p$.e && (_p$.e = _$setProp(_el$34, "fg", _v$10, _p$.e));
                    _v$11 !== _p$.t && (_p$.t = _$setProp(_el$36, "style", _v$11, _p$.t));
                    return _p$;
                  }, {
                    e: undefined,
                    t: undefined
                  });
                  return _el$34;
                })()];
              }
            }), _$createComponent(Match, {
              get when() {
                return store.mode === "shell";
              },
              get children() {
                var _el$38 = _$createElement("text"),
                  _el$39 = _$createTextNode(`esc `),
                  _el$40 = _$createElement("span");
                _$insertNode(_el$38, _el$39);
                _$insertNode(_el$38, _el$40);
                _$insertNode(_el$40, _$createTextNode(`exit shell mode`));
                _$effect(_p$ => {
                  var _v$12 = theme.text,
                    _v$13 = {
                      fg: theme.textMuted
                    };
                  _v$12 !== _p$.e && (_p$.e = _$setProp(_el$38, "fg", _v$12, _p$.e));
                  _v$13 !== _p$.t && (_p$.t = _$setProp(_el$40, "style", _v$13, _p$.t));
                  return _p$;
                }, {
                  e: undefined,
                  t: undefined
                });
                return _el$38;
              }
            })];
          }
        }));
        return _el$25;
      }
    }), null);
    _$effect(_p$ => {
      var _v$14 = props.visible !== false,
        _v$15 = highlight(),
        _v$16 = {
          ...EmptyBorder,
          vertical: "┃",
          bottomLeft: "╹"
        },
        _v$17 = theme.backgroundElement,
        _v$18 = placeholderText(),
        _v$19 = keybind.leader ? theme.textMuted : theme.text,
        _v$20 = keybind.leader ? theme.textMuted : theme.text,
        _v$21 = textareaKeybindings(),
        _v$22 = theme.backgroundElement,
        _v$23 = theme.text,
        _v$24 = syntax(),
        _v$25 = highlight(),
        _v$26 = highlight(),
        _v$27 = {
          ...EmptyBorder,
          vertical: theme.backgroundElement.a !== 0 ? "╹" : " "
        },
        _v$28 = theme.backgroundElement,
        _v$29 = theme.backgroundElement.a !== 0 ? {
          ...EmptyBorder,
          horizontal: "▀"
        } : {
          ...EmptyBorder,
          horizontal: " "
        };
      _v$14 !== _p$.e && (_p$.e = _$setProp(_el$, "visible", _v$14, _p$.e));
      _v$15 !== _p$.t && (_p$.t = _$setProp(_el$2, "borderColor", _v$15, _p$.t));
      _v$16 !== _p$.a && (_p$.a = _$setProp(_el$2, "customBorderChars", _v$16, _p$.a));
      _v$17 !== _p$.o && (_p$.o = _$setProp(_el$3, "backgroundColor", _v$17, _p$.o));
      _v$18 !== _p$.i && (_p$.i = _$setProp(_el$4, "placeholder", _v$18, _p$.i));
      _v$19 !== _p$.n && (_p$.n = _$setProp(_el$4, "textColor", _v$19, _p$.n));
      _v$20 !== _p$.s && (_p$.s = _$setProp(_el$4, "focusedTextColor", _v$20, _p$.s));
      _v$21 !== _p$.h && (_p$.h = _$setProp(_el$4, "keyBindings", _v$21, _p$.h));
      _v$22 !== _p$.r && (_p$.r = _$setProp(_el$4, "focusedBackgroundColor", _v$22, _p$.r));
      _v$23 !== _p$.d && (_p$.d = _$setProp(_el$4, "cursorColor", _v$23, _p$.d));
      _v$24 !== _p$.l && (_p$.l = _$setProp(_el$4, "syntaxStyle", _v$24, _p$.l));
      _v$25 !== _p$.u && (_p$.u = _$setProp(_el$6, "fg", _v$25, _p$.u));
      _v$26 !== _p$.c && (_p$.c = _$setProp(_el$13, "borderColor", _v$26, _p$.c));
      _v$27 !== _p$.w && (_p$.w = _$setProp(_el$13, "customBorderChars", _v$27, _p$.w));
      _v$28 !== _p$.m && (_p$.m = _$setProp(_el$14, "borderColor", _v$28, _p$.m));
      _v$29 !== _p$.f && (_p$.f = _$setProp(_el$14, "customBorderChars", _v$29, _p$.f));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined,
      i: undefined,
      n: undefined,
      s: undefined,
      h: undefined,
      r: undefined,
      d: undefined,
      l: undefined,
      u: undefined,
      c: undefined,
      w: undefined,
      m: undefined,
      f: undefined
    });
    return _el$;
  })()];
}